import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Flex,
  Avatar,
  Button,
  Spinner,
  Input,
  useToast,
  Image,
} from "@chakra-ui/react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";
import { FaSync, FaPrint } from "react-icons/fa";
import { FaTrash } from "react-icons/fa"; // Import trash icon
import { Link } from "react-router-dom";
import { BASE_URL } from "../../Globalapi/Globalapi";

const HouseAssigning = () => {
  const toast = useToast();

  const [students, setStudents] = useState([]); // Unassigned students
  const [allStudents, setAllStudents] = useState([]); // Keep track of all students
  const [houses, setHouses] = useState([]); // List of houses
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [error, setError] = useState(null);
  const userid = sessionStorage.getItem("user_id");

  // Fetch only unassigned students
  const fetchUnassignedStudents = async () => {
    console.log("Fetching unassigned students...");
    try {
      const response = await axios.post(
        `${BASE_URL}/staff/class/class_students.php`,
        { pass: userid, filter: "unassigned" } // Filter for unassigned students
      );
      console.log("Fetched unassigned students:", response.data);
      const fetchedStudents = response.data.data || [];
      setStudents(fetchedStudents); // Only set unassigned students
      setAllStudents(fetchedStudents); // Keep track of all students
    } catch (error) {
      console.error("Error fetching unassigned students:", error);
      setError("Error fetching students. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  const filteredStudents = students.filter(
    (student) =>
      student &&
      student.student_name &&
      student.student_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Fetch houses and assigned students
  const fetchHousesAndAssignedStudents = async () => {
    console.log("Fetching houses and assigned students...");
    try {
      const houseResponse = await axios.get(
        `${BASE_URL}/admin/house/list_all_house.php`
      );
      console.log("Fetched houses:", houseResponse.data);
      const fetchedHouses =
        houseResponse.data.events.map((house) => ({
          ...house,
          assignedStudents: [], // Initialize with empty assigned students
        })) || [];
      setHouses(fetchedHouses);

      const assignedResponse = await axios.post(
        `${BASE_URL}/admin/house/list_assigned_students.php`,
        { pass: userid }
      );
      console.log("Fetched assigned students:", assignedResponse.data);

      if (assignedResponse.data.success) {
        const assignments = assignedResponse.data.data || [];
        const updatedHouses = fetchedHouses.map((house) => {
          const assignedStudents = assignments
            .filter((assignment) => assignment.house_id === house.id)
            .map((assignment) => {
              return {
                student_id: assignment.student_id,
                id: assignment.id, // Include the assignment ID here
              };
            });

          return {
            ...house,
            assignedStudents,
          };
        });

        setHouses(updatedHouses);

        // Filter out students who are already assigned to houses
        const assignedStudentIds = assignments.map((a) => a.student_id);
        setStudents((prevStudents) =>
          prevStudents.filter(
            (student) => !assignedStudentIds.includes(student.student_id)
          )
        );
      }
    } catch (error) {
      console.error("Error fetching houses or assigned students:", error);
      setError("Error fetching data. Please try again later.");
    }
  };

  // Fetch unassigned students and houses on component mount
  useEffect(() => {
    setLoading(true);
    fetchUnassignedStudents();
    fetchHousesAndAssignedStudents();
  }, [userid]);

  // Capitalize each word in a string
  const capitalize = (str) => {
    if (typeof str !== "string") return ""; // Ensure input is a string
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Handle drag end (when a student is dragged and dropped)
  const handleDragEnd = (result) => {
    const { source, destination } = result;

    // Dropped outside the list
    if (!destination) return;

    const newHouses = [...houses];
    const studentId = result.draggableId;

    // If the student is being dragged from unassigned students
    if (source.droppableId === "students") {
      // Remove from unassigned students list
      setStudents((prevStudents) =>
        prevStudents.filter((student) => student.student_id !== studentId)
      );

      // Assign student to the new house
      const destinationHouse = newHouses.find(
        (house) => house.id.toString() === destination.droppableId
      );
      if (destinationHouse) {
        destinationHouse.assignedStudents.push({
          student_id: studentId,
          id: null, // Temporarily set ID as null
        });
      }
    } else {
      // If dragging from an assigned house
      const sourceHouse = newHouses.find(
        (house) => house.id.toString() === source.droppableId
      );

      if (sourceHouse) {
        // Remove student from the source house
        sourceHouse.assignedStudents = sourceHouse.assignedStudents.filter(
          (assignment) => assignment.student_id !== studentId
        );

        // Assign student to the new house
        const destinationHouse = newHouses.find(
          (house) => house.id.toString() === destination.droppableId
        );
        if (destinationHouse) {
          destinationHouse.assignedStudents.push({
            student_id: studentId,
            id: null, // Temporarily set ID as null
          });
        }
      }
    }

    // Update houses state
    setHouses(newHouses);
  };

  // Handle saving assignments
  const handleSave = async () => {
    console.log("Saving house assignments...");

    try {
      const assignments = houses.map((house) => ({
        house_id: house.id, // Ensure it matches your PHP expected keys
        student_ids: house.assignedStudents.map(
          (assignment) => assignment.student_id
        ),
      }));

      const response = await axios.post(
        `${BASE_URL}/admin/house/create_house_assigning.php`,
        { house_students: assignments, pass: userid }
      );

      if (response.status === 200) {
        toast({
          title: "Assignments Saved Successfully",
          description: "The assignments have been saved successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Clear the unassigned students list after a successful save
        setStudents([]);
      } else {
        throw new Error("Error saving assignments.");
      }
    } catch (error) {
      console.error("Error saving assignments:", error);
      toast({
        title: "Failed to Save Assignments",
        description: "Failed to save assignments. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    window.location.reload();
  };

  // Automate assigning students to houses (simple random assignment)
  const handleAutomate = () => {
    if (students.length === 0 || houses.length === 0) {
      toast({
        title: "No Students or Houses to Assign",
        description: "Please ensure there are students or houses to assign.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const newHouses = [...houses];
    let studentIndex = 0;

    // Loop through each student and assign them to houses in a round-robin manner
    while (studentIndex < students.length) {
      newHouses.forEach((house) => {
        const student = students[studentIndex];

        // Check if student and student_id are defined
        if (student && student.student_id && studentIndex < students.length) {
          house.assignedStudents.push({
            student_id: student.student_id,
            id: null, // Temporarily set ID as null
          });
          studentIndex++;
        }
      });
    }

    setStudents([]); // Clear unassigned students
    setHouses(newHouses); // Update house assignments
    toast({
      title: "All Students Assigned to Houses",
      description: "All students have been successfully assigned to houses.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  // Handle removing an assigned student from a house
  const handleRemoveStudent = async (studentId, houseId, id) => {
    console.log(
      "Removing student:",
      studentId,
      "from house:",
      houseId,
      "from :",
      id
    );

    // Find the house assignment based on house and student
    const houseAssignment = houses.find(
      (house) =>
        house.id === houseId &&
        house.assignedStudents.some((a) => a.student_id === studentId)
    );

    // Check if houseAssignment is found
    if (!houseAssignment) {
      toast({
        title: "No Assignment Found",
        description: "No such assignment exists.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Log the found house assignment for debugging
    console.log("House Assignment:", houseAssignment);

    // We need to get the actual ID used for the assignment removal
    const assignment = houseAssignment.assignedStudents.find(
      (a) => a.student_id === studentId
    );

    // Log the assigned ID to verify it
    console.log("Assigned ID:", assignment.id);

    // Check if assignment is valid
    if (!assignment || !assignment.id) {
      toast({
        title: "Invalid Assignment ID",
        description: "The assignment ID provided is invalid.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      // Correct payload structure
      const response = await axios.delete(
        `${BASE_URL}/admin/house/delete_assigned_students.php`,
        { data: { house_assigned_id: assignment.id } } // Use the assignment ID
      );

      if (response.data.success) {
        console.log(response.data.message); // Log success message
        // Remove the student from the state
        setHouses((prevHouses) =>
          prevHouses.map((house) => ({
            ...house,
            assignedStudents: house.assignedStudents.filter(
              (a) => a.student_id !== studentId
            ),
          }))
        );
        setStudents((prevStudents) => [
          ...prevStudents,
          allStudents.find((student) => student.student_id === studentId), // Add back to unassigned
        ]);
        toast({
          title: "Student Removed Successfully",
          description: "The student has been successfully removed.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Failed to Remove Student",
          description: "Failed to remove student. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error removing student:", error);
      toast({
        title: "Error Removing Student",
        description: "An error occurred while removing the student.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box padding={5}>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="20px" fontWeight="bold">
          House Creation
        </Text>
        <div className="action-buttons">
          {/* <Button
    backgroundColor="white"
    fontSize={{ base: "10px", md: "11px" }}
    boxShadow="md"
    marginRight={{ base: 1, md: 2 }}
    width={{ base: "100%", md: "auto" }}
    mb={{ base: 2, md: 0 }} // Margin bottom on small screens
  >
    <FaPrint color="#515B73" />
    Print PDF
  </Button> */}

          <Button
            leftIcon={<FaSync />}
            colorScheme="teal"
            onClick={handleAutomate}
            marginRight={{ base: 1, md: 2 }}
            width={{ base: "100%", md: "auto" }}
            mb={{ base: 2, md: 0 }}
          >
            Auto
          </Button>

          <Button
            colorScheme="teal"
            onClick={handleSave}
            leftIcon={<FaPrint />}
            width={{ base: "100%", md: "auto" }}
          >
            Save
          </Button>
        </div>
      </Flex>
      <div className="breadcrumb" style={{ marginBottom: "16px" }}>
        <Link to="/admin" style={{ textDecoration: "none", color: "inherit" }}>
          <span>Dashboard</span>
        </Link>{" "}
        / <span>School House</span> / <span>House Creation</span>
      </div>

      {loading ? (
        <Spinner />
      ) : error ? (
        <Text color="red.500">{error}</Text>
      ) : (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Flex direction={{ base: "column", md: "row" }} align="flex-start">
              <Droppable droppableId="students">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    // border="1px solid #d4c2c2"
                    borderRadius="md"
                    padding="5"
                    width={{ base: "100%", md: "255px" }} // Full width on small screens
                    marginRight="20px"
                    bgGradient="linear(to-br, teal.100, white)"
                    boxShadow="lg"
                  >
                    <Text
                      fontSize="md"
                      mb={2}
                      textAlign="center"
                      fontWeight="bold"
                      color="teal.600"
                    >
                      Students List
                    </Text>
                    <Input
                      placeholder="Search students..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      mb={3}
                    />
                    {filteredStudents.length > 0 ? (
                      filteredStudents.map((student, index) => (
                        <Draggable
                          key={student.student_id}
                          draggableId={student.student_id}
                          index={index}
                        >
                          {(provided) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              // border="1px solid #d4c2c2"
                              borderRadius="md"
                              padding="2"
                              marginBottom="2"
                              bg="teal.50"
                              _hover={{ bg: "teal.100" }}
                              transition="background 0.2s ease"
                            >
                              <Flex align="center" justify="flex-start">
                                <Avatar
                                  name={student.student_name}
                                  src={student.image_url}
                                  size="xs"
                                />
                                <Text
                                  marginLeft="10px"
                                  fontSize="small"
                                  borderColor="#d4c2c2"
                                >
                                  {capitalize(student.student_name)}
                                </Text>
                              </Flex>
                            </Box>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <Text>No unassigned students available.</Text>
                    )}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>

              {houses.map((house) => (
                <Droppable key={house.id} droppableId={house.id.toString()}>
                  {(provided) => (
                    <Box
                      mt={{ base: 2, md: 0 }} // Set mt=2 for small devices and 0 for larger ones
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      // border="1px solid #d4c2c2"
                      borderRadius="md"
                      padding="5"
                      width={{ base: "100%", md: "220px" }} // Full width on small screens
                      marginRight="20px"
                      bgGradient="linear(to-br, blue.100, white)"
                      boxShadow="lg"
                    >
                      <Text
                        fontSize="md"
                        mb={2}
                        textAlign="center"
                        fontWeight="bold"
                        color="blue.600"
                      >
                        {capitalize(house.caption)}
                      </Text>
                      {house.assignedStudents.length > 0 ? (
                        house.assignedStudents.map((assignment, index) => (
                          <Draggable
                            key={assignment.student_id}
                            draggableId={assignment.student_id}
                            index={index}
                          >
                            {(provided) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                // border="1px solid #d4c2c2"
                                borderRadius="md"
                                padding="2"
                                marginBottom="2"
                                position="relative"
                                bg="blue.50"
                                _hover={{ bg: "blue.100" }}
                                transition="background 0.2s ease"
                              >
                                <Flex align="center" justify="flex-start">
                                  <Avatar
                                    src={
                                      allStudents.find(
                                        (student) =>
                                          student.student_id ===
                                          assignment.student_id
                                      )?.image_url
                                    }
                                    name={capitalize(
                                      allStudents.find(
                                        (student) =>
                                          student.student_id ===
                                          assignment.student_id
                                      )?.student_name || ""
                                    )}
                                    size="xs"
                                    marginRight="10px"
                                  />
                                  <Box>
                                    <Text fontSize="small">
                                      {capitalize(
                                        allStudents.find(
                                          (student) =>
                                            student.student_id ===
                                            assignment.student_id
                                        )?.student_name || ""
                                      )}
                                    </Text>
                                  </Box>
                                  <Box
                                    as="span"
                                    position="absolute"
                                    top={1}
                                    right={1}
                                    cursor="pointer"
                                    onClick={() =>
                                      handleRemoveStudent(
                                        assignment.student_id,
                                        house.id,
                                        assignment.id
                                      )
                                    }
                                  >
                                    <FaTrash color="#d70707" size="12px" />
                                  </Box>
                                </Flex>
                              </Box>
                            )}
                          </Draggable>
                        ))
                      ) : (
                        <Text>No students assigned to this house.</Text>
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              ))}
            </Flex>
          </DragDropContext>
        </>
      )}
    </Box>
  );
};

export default HouseAssigning;
